import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../env';
import {ImageSearchQuery, ImageSearchResult, ImageSearchSubject} from '../../models/image-search';

/**
 * Image search: the user picks a picture for a product or a recipe from a
 * provider's results instead of the backend guessing one.
 *
 * Search and import hang off the owning resource, because that is where the
 * backend enforces permissions; only the preview proxy is global, since it
 * needs nothing but a signed candidate.
 */
@Injectable({providedIn: 'root'})
export class ImageSearchService {

  constructor(private http: HttpClient) {
  }

  /**
   * GET /api/{products|recipes}/:id/images/search
   *
   * Sending no `q` is the normal case: the backend derives the query from the
   * subject (a product's brand, name and barcode; a recipe's title).
   *
   * Fails with 501 when no provider is configured for this subject.
   */
  search(
    subject: ImageSearchSubject,
    entityId: number,
    query: ImageSearchQuery = {},
  ): Observable<ImageSearchResult> {
    let params = new HttpParams();
    if (query.q?.trim()) {
      params = params.set('q', query.q.trim());
    }
    if (query.page && query.page > 0) {
      params = params.set('page', query.page);
    }
    if (query.perPage && query.perPage > 0) {
      params = params.set('per_page', query.perPage);
    }
    if (query.provider) {
      params = params.set('provider', query.provider);
    }
    if (query.locale) {
      params = params.set('locale', query.locale);
    }

    return this.http.get<ImageSearchResult>(
      `${this.baseFor(subject, entityId)}/images/search`,
      {params},
    );
  }

  /**
   * GET /api/image-search/preview/:candidateId
   *
   * The URL is rebuilt from `environment.apiUrl` rather than taken from the
   * hit's absolute `preview_url`, so the request goes through the same proxy
   * and auth interceptor as every other call. The caller wraps the blob in an
   * object URL — the same approach as recipe-import previews.
   */
  preview(candidateId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/image-search/preview/${encodeURIComponent(candidateId)}`,
      {responseType: 'blob'},
    );
  }

  /**
   * POST /api/{products|recipes}/:id/images/import
   *
   * Imports the chosen result: the backend downloads it, generates the usual
   * variants, stores them in S3 and records where the image came from. Only the
   * signed candidate id travels — never a URL. A 410 means the candidate
   * outlived its token and the search has to be re-run.
   */
  import(
    subject: ImageSearchSubject,
    entityId: number,
    candidateId: string,
    isPrimary: boolean,
  ): Observable<any> {
    return this.http.post(
      `${this.baseFor(subject, entityId)}/images/import`,
      {candidate_id: candidateId, is_primary: isPrimary},
    );
  }

  private baseFor(subject: ImageSearchSubject, entityId: number): string {
    const collection = subject === 'recipe' ? 'recipes' : 'products';
    return `${environment.apiUrl}/${collection}/${entityId}`;
  }
}
